import React, {useEffect, useRef, useState} from 'react';
import {Alert, Button, Input, message, Spin, Tag} from 'antd';
import {CameraOutlined, CloseCircleFilled, CopyOutlined, PictureOutlined} from '@ant-design/icons';
import {useMutation, useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {ComplaintTaskDetailResponse, GET_COMPLAINT_TASK_DETAIL} from 'graphql/complaintTask/queries';
import {
  RESOLVE_COMPLAINT_TASK,
  ResolveComplaintTaskVariables,
  START_COMPLAINT_TASK,
} from 'graphql/complaintTask/mutations';
import {translateGraphQLError} from 'utils/graphqlUtils';
import {
  formatDateTime,
  formatTime,
  PRIORITY_COLOR,
  priorityLabel,
  subTypeLabel,
} from 'pages/qualitycontrol/complaintTask/complaintTaskUtils';
import {MessageContent, ResolutionView} from 'pages/qualitycontrol/complaintTask/components/ComplaintTaskDetailPanel';
import {useComplaintAttachmentUpload} from 'pages/qualitycontrol/complaintTask/useComplaintAttachmentUpload';

/** 客户原话默认只展开最近几条（PRD：2 条，可点开全部） */
const RECENT_MESSAGES = 2;

const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Lark 内置浏览器等不支持 Clipboard API 时兜底
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    ta.remove();
  }
};

interface Props {
  taskId: number;
  /** 是否在待处理队列里（不在 = 从卡片链接 / 已解决列表打开，只给「返回队列」） */
  inQueue: boolean;
  /** 卡片「开始处理」链接进来：加载后自动记为已响应，只执行一次 */
  autoStart: boolean;
  onAutoStartHandled: () => void;
  onStarted: (taskId: number) => void;
  onResolved: (taskId: number) => void;
  onSkip: (taskId: number) => void;
  onBack: () => void;
}

/**
 * 逐条处理的当前任务（PRD 手机版 Focus 模式）：等级 / 客户 / 概述 + 客户原话气泡 + 运单号点按复制；
 * 「开始处理」记响应后就地切成填结果（拍照 / 相册，单个 ≤5MB），「完成并关闭」解决任务。
 */
const MobileTaskCard: React.FC<Props> = ({
  taskId, inQueue, autoStart, onAutoStartHandled, onStarted, onResolved, onSkip, onBack,
}) => {
  const {t} = useTranslation();
  const [content, setContent] = useState('');
  const [showAllMessages, setShowAllMessages] = useState(false);
  const cameraRef = useRef<HTMLInputElement>(null);
  const albumRef = useRef<HTMLInputElement>(null);
  const autoStartDone = useRef(false);
  const {files, uploading, upload, remove, toInputs} = useComplaintAttachmentUpload(taskId);

  const {data, loading, error, refetch} = useQuery<ComplaintTaskDetailResponse>(GET_COMPLAINT_TASK_DETAIL, {
    variables: {taskId},
    fetchPolicy: 'network-only',
  });
  const [startTask, {loading: starting}] = useMutation(START_COMPLAINT_TASK);
  const [resolveTask, {loading: resolving}] = useMutation<boolean, ResolveComplaintTaskVariables>(RESOLVE_COMPLAINT_TASK);

  const detail = data?.complaintTaskDetail;
  const task = detail?.task;
  const unresponded = !!task && task.status === 40 && !task.actualStartAt;
  const processing = !!task && task.status === 40 && !!task.actualStartAt && !!detail?.canProcess;

  const start = async () => {
    try {
      await startTask({variables: {taskId}});
      await refetch();
      onStarted(taskId);
    } catch (e) {
      message.error(translateGraphQLError(e, 'complaintTask.errors.startFailed'));
    }
  };

  useEffect(() => {
    if (!autoStart || autoStartDone.current || !detail) return;
    autoStartDone.current = true;
    onAutoStartHandled();
    if (unresponded && detail.canProcess) {
      void start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, detail]);

  const resolve = async () => {
    try {
      await resolveTask({variables: {input: {taskId, content: content.trim(), attachments: toInputs()}}});
      message.success(t('complaintMobile.result.resolved'));
      onResolved(taskId);
    } catch (e) {
      message.error(translateGraphQLError(e, 'complaintTask.errors.resolveFailed'));
    }
  };

  const pickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    Array.from(e.target.files ?? []).forEach((f) => void upload(f));
    e.target.value = '';
  };

  if (error) {
    // 如卡片链接指向无权限 / 不存在的任务：给出原因，并能回到队列
    return (
      <div className="p-4 flex flex-col gap-3">
        <Alert type="error" showIcon message={translateGraphQLError(error, 'complaintTask.errors.loadFailed')}/>
        <Button block size="large" onClick={onBack}>{t('complaintMobile.action.back')}</Button>
      </div>
    );
  }
  if (!detail || !task) return <div className="py-24 text-center"><Spin spinning={loading}/></div>;

  const messages = showAllMessages ? detail.messages : detail.messages.slice(-RECENT_MESSAGES);

  return (
    <div className="pb-32">
      <div className="px-4 pt-3 flex flex-col gap-3">
        <div className="flex flex-wrap gap-1">
          <Tag color={PRIORITY_COLOR[task.priority ?? 3]}>{priorityLabel(task.priority)}</Tag>
          {task.emotionLevel === 'BURST' && <Tag color="red">{t('complaintTask.emotion.BURST')}</Tag>}
          <Tag color="purple">{subTypeLabel(task.subType, task.subTypeName)}</Tag>
          {task.ownerHubName && <Tag>{task.ownerHubName}</Tag>}
        </div>

        <div>
          <div className="text-base font-medium">
            {task.customerLevel && <Tag className="!mr-1">{task.customerLevel}</Tag>}
            {task.customerName ?? '—'}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {task.chatName ?? '—'} · {t('complaintTask.work.reported')} {formatDateTime(task.startAt)}
            {task.assigneeName && ` · ${t('complaintTask.columns.assignee')} ${task.assigneeName}`}
          </div>
        </div>

        <div className="text-lg leading-7">{task.title}</div>

        {task.trackingNos.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {task.trackingNos.map((no) => (
              <button key={no} type="button"
                      className="px-2 py-1 rounded bg-purple-50 text-purple-600 text-sm active:bg-purple-100"
                      onClick={() => copyText(no).then(() => message.success(t('complaintMobile.card.copied')))}>
                {no} <CopyOutlined/>
              </button>
            ))}
          </div>
        )}

        <div>
          <div className="text-sm text-gray-500 mb-2">{t('complaintMobile.card.messages')}</div>
          {detail.messages.length === 0 ? (
            <div className="text-sm text-gray-400">{t('complaintMobile.card.noMessages')}</div>
          ) : (
            <div className="flex flex-col gap-2">
              {messages.map((m, i) => (
                <div key={i} className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-6 ${
                  m.isCustomer === false ? 'self-end bg-purple-50' : 'self-start bg-gray-100'}`}>
                  <div className="text-xs text-gray-400">{m.speaker} · {formatTime(m.sentAt)}</div>
                  <div><MessageContent content={m.content}/></div>
                </div>
              ))}
              {detail.messages.length > RECENT_MESSAGES && (
                <button type="button" className="text-sm text-purple-600 self-start"
                        onClick={() => setShowAllMessages((v) => !v)}>
                  {showAllMessages
                    ? t('complaintMobile.card.collapse')
                    : t('complaintMobile.card.showAll', {count: detail.messages.length})}
                </button>
              )}
            </div>
          )}
        </div>

        <ResolutionView detail={detail}/>

        {!inQueue && task.status === 40 && (
          <Alert type="info" showIcon message={t('complaintMobile.card.notInQueue')}/>
        )}

        {processing && (
          <div className="flex flex-col gap-2">
            {/* SOP 提示浮窗位（P11）：内容后续由客服提供 */}
            <Input.TextArea
              autoSize={{minRows: 4, maxRows: 10}}
              value={content}
              placeholder={t('complaintMobile.result.placeholder') ?? ''}
              onChange={(e) => setContent(e.target.value)}
            />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {files.map((f) => (
                  <div key={f.fileKey} className="relative">
                    {f.previewUrl
                      ? <img src={f.previewUrl} alt={f.fileName} className="h-16 w-16 object-cover rounded"/>
                      : <div className="h-16 w-24 rounded bg-gray-100 text-xs p-1 break-all overflow-hidden">{f.fileName}</div>}
                    <CloseCircleFilled className="absolute -top-1 -right-1 text-gray-500 bg-white rounded-full"
                                       onClick={() => remove(f.fileKey)}/>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button icon={<CameraOutlined/>} loading={uploading} onClick={() => cameraRef.current?.click()}>
                {t('complaintMobile.action.camera')}
              </Button>
              <Button icon={<PictureOutlined/>} disabled={uploading} onClick={() => albumRef.current?.click()}>
                {t('complaintMobile.action.album')}
              </Button>
              <span className="text-xs text-gray-400 self-center">{t('complaintMobile.result.attachHint')}</span>
            </div>
            {/* 手机版只给拍照 / 相册，不提供通用附件按钮（PRD） */}
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={pickFiles}/>
            <input ref={albumRef} type="file" accept="image/*,video/*" multiple hidden onChange={pickFiles}/>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-white border-t px-4 pt-3 flex gap-3"
           style={{paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)'}}>
        {!inQueue || task.status !== 40 ? (
          <Button block size="large" onClick={onBack}>{t('complaintMobile.action.back')}</Button>
        ) : (
          <>
            <Button size="large" className="!w-28" onClick={() => onSkip(taskId)}>{t('complaintMobile.action.skip')}</Button>
            {unresponded ? (
              <Button block size="large" type="primary" loading={starting} disabled={!detail.canProcess} onClick={start}>
                {t('complaintMobile.action.start')}
              </Button>
            ) : (
              <Button block size="large" type="primary" className="!bg-green-600" loading={resolving}
                      disabled={!processing || !content.trim() || uploading} onClick={resolve}>
                {t('complaintMobile.action.resolve')}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MobileTaskCard;
